import { FC, useCallback, useState } from "react";
import { ActionList, Button, Popover, Spinner } from "@shopify/polaris";
import { HorizontalDotsMinor } from "@shopify/polaris-icons";
import { useNavigate } from "react-router-dom";
import { openModal } from "../../../../../../../store/features/modal.js";
import { OPEN_MODAL_SYNC, OPEN_MODAL_UNDO_CHANGES } from "../../../../../../../constants/modalTypes.js";
import { useAppMutation, useAppQuery } from "../../../../../../../hooks/index.js";
import { updateShop } from "../../../../../../../store/features/shop.js";
import { useAppDispatch, useAppSelector } from "../../../../../../../hooks/redux.js";
import { updateAction } from "../../../../../../../store/features/action.js";
import styles from "../ActionTable.module.css";

const ActionPopover = ({
  actionId,
  disabled,
  onSetAction,
  onUpdate,
  toggleUndoBanner,
  actionStatus,
  isUndoing,
  setIsDeleting,
  setShowBanner
}) => {
  const dispatch = useAppDispatch();
  const shop = useAppSelector(state => state.shop.data);
  const actionState = useAppSelector(state => state.action);
  const [updateStatus, setUpdateStatus] = useState(Boolean(disabled));
  const [popoverActive, setPopoverActive] = useState(false);
  const navigate = useNavigate();

  const { mutate: mutateUndoAll, isLoading: isLoadingUndoAll } = useAppMutation({
    url: "/api/action-history/undo-all",
    reactMutationOptions: {
      onSuccess() {
        showToast("All changes will be undone");
        toggleUndoBanner(false);
      },
      onError(error) {
        showToast(error ?? "Something went wrong during undo", true);
        toggleUndoBanner(false);
      }
    }
  });

  const {
    refetch: refetchAction,
    isLoading: isLoadingAction,
  } = useAppQuery({
    url: `/api/action/${actionId}`,
    reactQueryOptions: { enabled: false }
  });

  const { isLoading: isLoadingDelete, mutate: mutateDelete } = useAppMutation({
    url: "/api/action/delete",
    reactMutationOptions: {
      onSuccess(data) {
        setIsDeleting(false)
        onSetAction((prev) => prev.filter(action => action.id !== data.action.id));
        dispatch(updateShop({ ...shop, rejectedActions: shop.rejectedActions.filter(action => action !== data.action.id) }));
      }
    }
  });

  const { isLoading: isLoadingUpdate, mutate: mutateUpdate } = useAppMutation({
    url: "/api/action/manual",
    reactMutationOptions: {
      onSuccess() {
        showToast("Action started manually");
        setUpdateStatus(true)
        setShowBanner(false)
        onUpdate();
      },
      onError(error) {
        showToast(error ?? "Something went wrong", true);
        setShowBanner(false)
      }
    }
  });

  const showToast = (message, error = false) => {
    shopify.toast.show(message, { isError: error });
  };

  const togglePopoverActive = useCallback(
    () => setPopoverActive((popoverActive) => !popoverActive),
    []
  );

  const isSomeLoading = useCallback(() =>
    isLoadingAction || isLoadingUpdate || isLoadingDelete || isLoadingUndoAll,
    [isLoadingAction, isLoadingUpdate, isLoadingDelete, isLoadingUndoAll]
  );

  const activator = (
    <Button
      onClick={togglePopoverActive}
      icon={HorizontalDotsMinor}
      disabled={updateStatus || isUndoing}
    />
  );

  const handleEdit = useCallback(async () => {
    if (shop && (shop?.synchronizationStatus === "processing")) {
      dispatch(openModal({ modalType: OPEN_MODAL_SYNC, modalData: { triggerType: "EDIT_ACTION_BUTTON" } }));
      return;
    }
    const { data } = await refetchAction();
    const action = (data as any).action;
    dispatch(updateAction({
      ...actionState,
      actionId: action.id,
      currentStep: 1,
      actionData: {
        name: action.name,
        type: action.type,
        updatesFrequency: action.updatesFrequency,
        selectedProducts: action.selectedProducts,
        options: action.options,
        advancedSetting: action.advancedSetting,
      }
    }));
    navigate("/edit");
  }, [actionId, actionState, shop, dispatch]);

  const handleUndoChanges = useCallback(() => {
    toggleUndoBanner(true);
    togglePopoverActive()
    mutateUndoAll({ actionId });
  }, [actionId, mutateUndoAll, toggleUndoBanner]);

  const handleDelete = useCallback(() => {
    setIsDeleting(true)
    mutateDelete({ actionId });
  }, [actionId, mutateDelete]);

  const handleUpdate = useCallback(() => {
    setShowBanner(true)
    mutateUpdate({ actionId });
  }, [actionId, mutateUpdate]);

  const handleViewHistory = useCallback(() => {
    dispatch(openModal({ modalType: OPEN_MODAL_UNDO_CHANGES, modalData: { actionId } }));
  }, [dispatch, actionId]);

  const items = [
    { content: isLoadingUpdate ? <Spinner size="small" /> : "Update", disabled: isSomeLoading() || !actionStatus, onAction: handleUpdate, className: !actionStatus ? styles["custom-disabled-item"] : "" },
    { content: isLoadingAction ? <Spinner size="small" /> : "Edit", disabled: isSomeLoading(), onAction: handleEdit },
    { content: "View History", disabled: isSomeLoading(), onAction: handleViewHistory },
    { content: isLoadingDelete ? <Spinner size="small" /> : "Delete", disabled: isSomeLoading(), onAction: handleDelete },
    { content: "Undo changes", disabled: isSomeLoading(), onAction: handleUndoChanges }
  ];

  return (
    <Popover active={popoverActive && !updateStatus} activator={activator} autofocusTarget="first-node" onClose={togglePopoverActive}>
      <ActionList
        // @ts-ignore
        items={items}
      />
    </Popover>
  );
};

export default ActionPopover;
